import { useAppSelector } from 'app/hooks';
import Modal from 'components/common/modal';
import Tag from 'components/common/tag';
import InfoTooltip from 'components/layout/info-tooltip';
import ProductPhoto from 'components/products/product-photo';
import { SelectedSystemType } from 'data/types';
import { Dispatch, SetStateAction } from 'react';
import { formatUSD } from 'utils/helpers';

type RebatesModalProps = {
  openState: [boolean, Dispatch<SetStateAction<boolean>>];
  system: SelectedSystemType | null;
};

export default function RebatesModal({ openState, system }: RebatesModalProps) {
  const { condenserProducts, airHandlerProducts } = useAppSelector(
    (state) => state.data
  );
  const { locationData } = useAppSelector((state) => state.project);
  const { rebatesForModal } = useAppSelector((state) => state.ui);

  const productQuantities =
    system?.configuration.condensers.reduce((acc, condenser) => {
      if (condenser.selectedProduct) {
        acc[condenser.selectedProduct.productId] =
          (acc[condenser.selectedProduct.productId] || 0) + 1;
      }
      condenser.zones.forEach((zone) => {
        if (zone.selectedProduct) {
          acc[zone.selectedProduct.productId] =
            (acc[zone.selectedProduct.productId] || 0) + 1;
        }
      });
      return acc;
    }, {} as Record<number, number>) || {};

  return (
    <Modal
      title={`Available Rebates for ${
        locationData?.location ?? 'Your Location'
      }`}
      openState={openState}
      infoMode
    >
      <div className='flex flex-col gap-5'>
        {rebatesForModal.map((rebate) => {
          const { rebateMinimum, rebateMaximum } = rebate;
          const product =
            airHandlerProducts.find((p) => p.productId === rebate.productId) ??
            condenserProducts.find((p) => p.productId === rebate.productId);
          const quantity = productQuantities[rebate.productId] || 1;
          return (
            <div key={rebate.productId} className='flex flex-col gap-5'>
              <div className='flex items-center gap-2'>
                {product && <ProductPhoto product={product} />}
                <div className='grow'>
                  <div className='flex items-center justify-between gap-5 text-lg'>
                    <span>
                      {product?.name ?? rebate.productName}{' '}
                      {quantity > 1 && `(${quantity} units)`}
                    </span>
                    <span>{formatUSD(rebate.price * quantity)}</span>
                  </div>
                  <div className='flex items-center justify-between gap-5 text-lg font-semibold'>
                    <div className='inline-flex items-center gap-1'>
                      <span className='whitespace-nowrap'>
                        Potential Rebates
                      </span>
                      <InfoTooltip>
                        Your eligibility for specific rebates and tax credits
                        may depend on various criteria. Please see the
                        individual rebate or tax credit for details.
                      </InfoTooltip>
                    </div>
                    <span>
                      {formatUSD(rebateMinimum * quantity)}
                      {rebateMinimum !== rebateMaximum &&
                        ` - ${formatUSD(rebateMaximum * quantity)}`}
                    </span>
                  </div>
                </div>
              </div>
              <div className='flex flex-col gap-5'>
                {rebate.programs.map((program) => (
                  <div
                    key={program.id}
                    className='flex flex-col gap-3 rounded-md border-2 border-gray-300 bg-gray-100 p-3'
                  >
                    <div className='flex items-start justify-between gap-5 text-xl font-semibold'>
                      <span>{program.name}</span>
                      <span className='whitespace-nowrap text-right'>
                        up to {formatUSD((program.min / 100) * quantity)}
                        {program.max &&
                          program.min !== program.max &&
                          ` - ${formatUSD((program.max / 100) * quantity)}`}
                      </span>
                    </div>
                    <div className='flex flex-wrap gap-2'>
                      {program.tags.map((tag) => (
                        <Tag
                          key={tag.name}
                          text={tag.display_name.trim()}
                          customColor={tag.color_hex}
                          textColor='white'
                        />
                      ))}
                    </div>
                    <div className='flex flex-col'>
                      <div className='font-semibold'>Program Details:</div>
                      <div>
                        {program.description}{' '}
                        {program.link && (
                          <a
                            className='font-semibold text-blue-500 underline hover:text-blue-600'
                            href={program.link}
                          >
                            Learn More
                          </a>
                        )}
                      </div>
                    </div>
                    <div className='flex flex-col'>
                      <div className='font-semibold'>How to Redeem:</div>
                      <div>
                        {program.apply_description}{' '}
                        {program.app_link_digital && (
                          <a
                            className='font-semibold text-blue-500 underline hover:text-blue-600'
                            href={program.app_link_digital}
                          >
                            Apply Here
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
